//# IMPORTS

// Project imports
import AIChatMessage from "../AIChatMessage/AIChatMessage";
import { useAIChatStickToBottom, useAIChatScrollToPrompt } from "@/utilities/hooks";
import { isSafari } from "@/utilities/misc";
import styles from "./AIChatConversationView.module.css";

// Vendor imports
import { ActionIcon, Box, Center, Group, Loader, ScrollArea, Text, Tooltip } from "@mantine/core";
import { Key } from "react";
import "katex/dist/katex.css";
import { IconArrowDown } from "@tabler/icons-react";

//# COMPONENT

interface AIChatConversationViewProps {
	messages: any;
	statusText: string | null;
	isStreaming: boolean;
	resendMessage: (index: any) => void;
	filterMessage: (message: any) => string;
	byColorScheme: any;
	utmSource: string;
	utmMedium: string;
	utmCampaign: string;
	[x: string]: any;
}

export function AIChatConversationView({
	messages,
	statusText,
	isStreaming,
	filterMessage,
	resendMessage,
	utmSource,
	utmMedium,
	utmCampaign,
	...customProps
}: AIChatConversationViewProps) {
	// Initialize auto-scroll behavior
	const [viewportRef, contentRef, streamingMessageRef, atBottom, scrollToBottom] = isSafari()
		? useAIChatScrollToPrompt(messages, styles)
		: useAIChatStickToBottom();

	return (
		<ScrollArea
			viewportRef={viewportRef}
			className={styles.root}
			type={isStreaming ? "auto" : "hover"}
			{...customProps}
		>
			<Box ref={contentRef}>
				{messages.map((message: any, index: Key | null | undefined) => (
					<AIChatMessage
						key={index}
						message={message}
						index={index}
						streamingMessageRef={streamingMessageRef}
						streamingMessageIndex={messages.length - 1}
						filterMessage={filterMessage}
						resendMessage={resendMessage}
						utmSource={utmSource}
						utmMedium={utmMedium}
						utmCampaign={utmCampaign}
					/>
				))}
				{statusText != null && (
					<Group className={styles.status} gap="xs" mt="md" mb="md" justify="center">
						<Loader size="md" type="dots" />
						<Text c="dimmed">{statusText}</Text>
					</Group>
				)}
			</Box>
			{!atBottom && (
				<Center>
					<Tooltip label="Go to bottom">
						<ActionIcon
							className={styles.buttonGoToBottom}
							autoContrast
							color="gray"
							size="lg"
							onClick={() => scrollToBottom()}
						>
							<IconArrowDown size="100%" />
						</ActionIcon>
					</Tooltip>
				</Center>
			)}
		</ScrollArea>
	);
}
